/**
 * Hero Section - Premium Enterprise Design
 *
 * Layout Strategy:
 * - LEFT (55%): Light, airy content area with lots of whitespace
 * - RIGHT (45%): Contained dark nebula zone with logo staging
 * - TRANSITION: Soft diagonal blend between light and dark areas
 *
 * Background Layers (via pseudo-elements):
 * 1. Base: Light off-white gradient across entire hero
 * 2. Right overlay: Dark navy gradient constrained to right side
 * 3. Blend mask: Diagonal gradient fade for soft transition
 * 4. Particle overlay: Sparse stars only in dark zone
 */

// Static site images should use direct public folder references
// import { getImageUrl } from "@/config/storage";

const HeroNew = () => {
  return (
    <section className="relative w-full">
      {/* CONTENT CONTAINER - Video background is now fixed globally */}
      <div className="relative z-10 bg-white/90 rounded-3xl shadow-2xl px-6 py-8 sm:px-10 sm:py-12 lg:px-16 lg:py-16 flex flex-col">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 w-full items-center flex-1">
          {/* LEFT COLUMN: Text Stack (60% - 7 cols) */}
          <div className="lg:col-span-7 space-y-6 lg:space-y-8">
            {/* HEADLINE: Multi-weight hierarchy */}
            <div className="space-y-3">
              <h1 className="text-4xl sm:text-5xl lg:text-7xl leading-[1.1] tracking-tight">
                <span className="block text-slate-600 font-light mb-2">
                  Break the
                </span>
                <span className="block text-[#B87333] font-bold mb-2">
                  Profitability
                </span>
                <span className="block text-slate-900 font-black">
                  Paradox.
                </span>
              </h1>
            </div>

            {/* SUBHEADLINE */}
            <p className="text-lg sm:text-xl lg:text-2xl text-slate-700 font-medium leading-relaxed max-w-[540px]">
              Scale revenue without sacrificing margins.
            </p>

            {/* BULLET ROW: Two-column grid */}
            <div className="grid grid-cols-1 gap-3 max-w-[540px]">
              <div className="flex items-center gap-3">
                <svg
                  className="w-5 h-5 text-[#B87333] flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-slate-700 font-medium">
                  Milestone-based pricing, not never-ending retainers
                </span>
              </div>
              <div className="flex items-center gap-3">
                <svg
                  className="w-5 h-5 text-[#B87333] flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-slate-700 font-medium">
                  Enterprise-grade execution
                </span>
              </div>
              <div className="flex items-center gap-3">
                <svg
                  className="w-5 h-5 text-[#B87333] flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-slate-700 font-medium">
                  Zero surprises
                </span>
              </div>
            </div>

            {/* CTA ROW */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 max-w-[540px]">
              <a
                href="https://calendar.notion.so/meet/aigeniusconsulting/793ez04oz7"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 sm:px-8 sm:py-4 bg-[#B87333] text-white rounded-lg font-semibold text-sm sm:text-base hover:bg-[#A66929] transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02]"
              >
                Book a Strategy Call
                <svg
                  className="w-5 h-5 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </a>
              <a
                href="/case-studies"
                className="inline-flex items-center justify-center px-6 py-3 sm:px-8 sm:py-4 bg-slate-100 text-slate-900 border-2 border-slate-300 rounded-lg font-semibold text-sm sm:text-base hover:bg-slate-200 hover:border-slate-400 transition-all duration-200 shadow-lg"
              >
                See Our Work
              </a>
            </div>

            {/* TRUST METRICS ROW: Small, understated */}
            <div className="flex flex-wrap gap-6 text-sm text-slate-600 max-w-[640px] pt-6 border-t border-slate-200">
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-slate-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
                  />
                </svg>
                <span className="drop-shadow">Custom ERP and AI systems</span>
              </div>
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-white/60"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <span className="drop-shadow">10+ industries served</span>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Logo (45% - 5 cols) */}
          <div className="lg:col-span-5 flex items-center justify-center relative">
            <div className="relative w-full max-w-[500px] flex flex-col items-center justify-center">
              <img
                src="/aig_consulting_logos_v4_darkLogoFull.png"
                alt="AI Genius Automations - AI-Empowered People and Businesses"
                loading="eager"
                className="w-full h-auto max-w-[450px]"
              />
              <p className="mt-6 text-slate-600 text-lg font-medium text-center italic">
                building intelligent software for ROI-focused businesses
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroNew;
